import React from "react";
import { AlertTriangle, RefreshCw, Trash2 } from "lucide-react";

/**
 * ErrorBoundary global de LEGATUM SAAS.
 *
 * Captura errores de render de cualquier ruta o componente y muestra una pantalla
 * de recuperación. Sin esto, React 19 desmonta TODO el árbol y el usuario ve un
 * blanco total (problema reportado en múltiples navegadores).
 *
 * Acciones que ofrece:
 *   - Recargar la página (intento simple).
 *   - Limpiar caché del navegador + Service Workers y recargar (hard reset).
 *   - Mostrar/copiar el stack para soporte.
 *
 * Casos típicos de captura:
 *   - ChunkLoadError tras un redeploy (el bundle hash cambió mientras el usuario
 *     tenía la pestaña abierta).
 *   - TypeError por respuesta API inesperada.
 *   - Errores de undefined en mapeos sobre listas.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log estructurado para depuración (visible en Console y, si hay backend de logs, también allí)
    // No bloquear render: simplemente notificar.
    try {
      // eslint-disable-next-line no-console
      console.error("[LEGATUM] ErrorBoundary captured:", error, info);
      // Enviar al endpoint de auditoría front (no requerido, fire-and-forget)
      const ev = {
        type: "frontend_render_error",
        message: String(error?.message || error),
        stack: String(error?.stack || ""),
        componentStack: String(info?.componentStack || ""),
        url: window.location.href,
        ua: navigator.userAgent,
        ts: new Date().toISOString(),
      };
      const beacon = JSON.stringify(ev);
      // Envío silencioso: no rompe si falla
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/_front_error", new Blob([beacon], { type: "application/json" }));
      }
    } catch (_e) {
      /* swallow */
    }
  }

  hardReset = async () => {
    try {
      // 1. Desregistrar TODOS los Service Workers
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // 2. Borrar todos los caches del navegador
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // 3. Limpiar localStorage/sessionStorage SALVO el token (para no forzar relogin gratuito)
      try {
        const keep = {};
        ["token", "legatum-theme"].forEach((k) => { const v = localStorage.getItem(k); if (v) keep[k] = v; });
        localStorage.clear();
        Object.entries(keep).forEach(([k, v]) => localStorage.setItem(k, v));
        sessionStorage.clear();
      } catch (_e) { /* swallow */ }
    } finally {
      // 4. Recarga forzada bypaseando caché (timestamp en query)
      const u = new URL(window.location.href);
      u.searchParams.set("_hr", String(Date.now()));
      window.location.replace(u.toString());
    }
  };

  softReload = () => {
    const u = new URL(window.location.href);
    u.searchParams.set("_r", String(Date.now()));
    window.location.replace(u.toString());
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const isChunkError = /loading chunk|loading css chunk|ChunkLoadError/i.test(
      String(this.state.error?.message || this.state.error?.name || "")
    );
    return (
      <div
        style={{
          minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg, #0F1419 0%, #1A1F2E 100%)",
          color: "#E8E6E1", fontFamily: "system-ui, -apple-system, sans-serif", padding: "2rem",
        }}
        data-testid="error-boundary"
      >
        <div style={{
          maxWidth: 560, width: "100%",
          background: "rgba(232, 230, 225, 0.05)",
          border: "1px solid rgba(212, 175, 55, 0.25)",
          borderRadius: 16, padding: "2rem", backdropFilter: "blur(12px)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <AlertTriangle size={28} color="#E08A1F" />
            <div>
              <div style={{ fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: "#D4AF37", fontWeight: 700 }}>
                LEGATUM CAPITAL · SAAS
              </div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>
                {isChunkError ? "Nueva versión disponible" : "Se ha producido un error inesperado"}
              </div>
            </div>
          </div>

          <p style={{ fontSize: 14, color: "#B8B5AE", lineHeight: 1.6, marginBottom: 20 }}>
            {isChunkError
              ? "Se ha desplegado una nueva versión del SAAS y tu navegador está cacheando la anterior. Pulsa “Limpiar caché y recargar” para actualizar."
              : "La página ha tropezado con un error que ha impedido continuar. Tus datos están seguros: solo se ha detenido la vista en pantalla. Recarga o limpia la caché para volver."}
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            <button
              type="button"
              onClick={this.softReload}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "#D4AF37", color: "#0F1419", border: "none",
                padding: "10px 16px", borderRadius: 10, fontWeight: 700, cursor: "pointer",
                fontSize: 13,
              }}
              data-testid="error-boundary-reload"
            >
              <RefreshCw size={14} /> Recargar página
            </button>
            <button
              type="button"
              onClick={this.hardReset}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "transparent", color: "#E8E6E1",
                border: "1px solid rgba(212, 175, 55, 0.45)",
                padding: "10px 16px", borderRadius: 10, fontWeight: 700, cursor: "pointer",
                fontSize: 13,
              }}
              data-testid="error-boundary-hard-reset"
            >
              <Trash2 size={14} /> Limpiar caché y recargar
            </button>
            <button
              type="button"
              onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
              style={{
                background: "transparent", color: "#8A8680",
                border: "none", padding: "10px 8px", cursor: "pointer", fontSize: 12, textDecoration: "underline",
              }}
              data-testid="error-boundary-toggle-details"
            >
              {this.state.showDetails ? "Ocultar detalles técnicos" : "Ver detalles técnicos"}
            </button>
          </div>

          {this.state.showDetails && (
            <pre
              style={{
                background: "#0A0E14", color: "#9AA0A6",
                padding: 12, borderRadius: 8, fontSize: 11,
                maxHeight: 220, overflow: "auto",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
              data-testid="error-boundary-stack"
            >
              {String(this.state.error?.message || this.state.error)}
              {"\n\n"}
              {String(this.state.error?.stack || "(sin stack)")}
            </pre>
          )}

          <div style={{ marginTop: 16, fontSize: 11, color: "#6E6A65", textAlign: "center" }}>
            Si el problema persiste tras limpiar caché, contacta a soporte adjuntando los detalles técnicos.
          </div>
        </div>
      </div>
    );
  }
}
